<template>
  <div class="jim-scheduling-container pnl-page">

    <!-- Header row (matches your vibe) -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">Comprehensive P&amp;L REPORT</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- ✅ LEFT SIDE CONTROLS (Pablo style) -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Start Date</label>
          <input v-model="startDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>End Date</label>
          <input v-model="endDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>CU</label>
          <select v-model="cu" class="select-input">
            <option value="ALL">ALL</option>
            <option value="00">00</option>
            <option value="01">01</option>
            <option value="02">02</option>
            <option value="03">03</option>
            <option value="04">04</option>
            <option value="05">05</option>
            <option value="09">09</option>
          </select>
        </div>

        <div class="control" style="width:100%;">
          <label>Options</label>
          <div class="toggle-row">
            <label class="toggle">
              <input type="checkbox" v-model="showPercent" />
              <span>Show %</span>
            </label>
            <label class="toggle">
              <input type="checkbox" v-model="showDetails" />
              <span>Show Detail</span>
            </label>
          </div>
        </div>

        <button class="plan-it-btn" :disabled="loading || !canRun" @click="runReport">
          {{ loading ? "Loading..." : "Run Report" }}
        </button>

        <button class="plan-it-btn" :disabled="!reportLoaded" @click="printPNL">
          Print
        </button>

        <button class="plan-it-btn" :disabled="!reportLoaded" @click="exportCSV">
          Export CSV
        </button>

        <!-- ✅ Unmapped accounts -->
        <div v-if="reportLoaded && (mtdReport?.unmapped_accounts?.length || 0) > 0"
            class="panel warn-box"
            style="margin-top:10px;">
          <div class="warn-title">
            Unmapped Accounts ({{ mtdReport.unmapped_accounts.length }})
          </div>
          <div class="warn-body">
            <div class="warn-list">
              <span v-for="a in mtdReport.unmapped_accounts" :key="a" class="warn-chip">
                {{ a }}
              </span>
            </div>
            <div class="small-muted" style="margin-top:8px;">
              These accounts are included in totals only if your backend logic sums them via section filters.
              Add them to <code>gl_pnl_map</code> to control display + ordering.
            </div>
          </div>
        </div>


      </div>

      <!-- ✅ RIGHT SIDE CONTENT (scrollable) -->
      <div class="right-side-section">

        <!-- Summary bar -->
        <div v-if="reportLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">MTD</div>
            <div class="summary-value">{{ mtdReport.startDate }} → {{ mtdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">YTD</div>
            <div class="summary-value">{{ ytdReport.startDate }} → {{ ytdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">LYTD</div>
            <div class="summary-value">{{ lytdReport.startDate }} → {{ lytdReport.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">CU</div>
            <div class="summary-value">{{ mtdReport.cu }}</div>
          </div>
        </div>

        


        <!-- Scrollable content wrapper -->
        <div v-if="reportLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">

            <div class="panel report-panel">

              <div class="table-scroll">
                <table class="pnl-table">
                  <thead>
                    <tr>
                      <th class="sticky-col line-col">Line</th>
                      <th class="month-col">{{ displayMonthLabel }}</th>
                      <th class="pct-col">%</th>
                      <th class="month-col">YTD</th>
                      <th class="pct-col">%</th>
                      <th class="month-col">LYTD</th>
                      <th class="pct-col">%</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in renderedRows" :key="row.key" :class="rowClass(row)">
                      <td class="sticky-col line-col">
                        <span :class="{ indent: row.indent }">{{ row.label }}</span>
                      </td>

                      <!-- MTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'MTD')">
                        <span v-if="cellAmount(row, monthObj) !== null">
                          {{ formatMoney(cellAmount(row, monthObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, monthObj) !== null">{{ formatPercent(cellPercent(row, monthObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>

                      <!-- YTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'YTD')">
                        <span v-if="cellAmount(row, ytdTotalsObj) !== null">
                          {{ formatMoney(cellAmount(row, ytdTotalsObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, ytdTotalsObj) !== null">{{ formatPercent(cellPercent(row, ytdTotalsObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>

                      <!-- LYTD -->
                      <td class="amt-cell clickable-cell"
                          @click="isClickable(row) && openDrilldown(row, 'LYTD')">
                        <span v-if="cellAmount(row, lytdTotalsObj) !== null">
                          {{ formatMoney(cellAmount(row, lytdTotalsObj)) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                      <td class="pct-cell">
                        <span v-if="cellPercent(row, lytdTotalsObj) !== null">{{ formatPercent(cellPercent(row, lytdTotalsObj)) }}</span>
                        <span v-else class="dash">-</span>
                      </td>
                    </tr>
                  </tbody>

                </table>
              </div>

              <!-- quick note -->
              <div class="small-muted" style="margin-top:10px;">
                
                Percent columns use <code>percent_of_gross_sales</code> when available.
              </div>

            </div>
          </div>
        </div>

        <div v-else class="panel hint-panel">
          Select a date range and CU, then click <b>Run Report</b>.
        </div>

      </div>
    </div>


        <!-- ✅ Drilldown Modal -->
        <div v-if="drillOpen" class="modal-overlay" @click.self="closeDrilldown">
          <div class="modal panel">
            <div class="modal-header">
              <div class="modal-title">
                Drilldown: {{ drillRowLabel }}
              </div>
              <button class="modal-close" @click="closeDrilldown">✕</button>
            </div>

            <div class="small-muted" style="margin-top:6px;">
              Period: <b>{{ drillPeriod }}</b> •
              Date: <b>{{ drillParams.startDate }}</b> → <b>{{ drillParams.endDate }}</b> •
              CU: <b>{{ cu }}</b>
            </div>

            <div v-if="drillLoading" class="panel" style="margin-top:10px;">
              Loading drilldown...
            </div>

            <div v-else-if="drillError" class="panel error-box" style="margin-top:10px;">
              {{ drillError }}
            </div>

            <div v-else-if="drillData" style="margin-top:10px;">

              <!-- Top Vendors -->
              <div v-if="(drillData.top_vendors?.length || 0) > 0" class="panel" style="margin-bottom:10px;">
                <div class="warn-title">Top Vendors</div>
                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th style="text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="v in drillData.top_vendors" :key="v.vendor_no">
                        <td>{{ v.vendor_name || v.vendor_no }}</td>
                        <td style="text-align:right;">{{ formatMoney(v.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Top Customers -->
              <div v-if="(drillData.top_customers?.length || 0) > 0" class="panel" style="margin-bottom:10px;">
                <div class="warn-title">Top Customers</div>
                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th style="text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in drillData.top_customers" :key="c.customer_no">
                        <td>{{ c.customer_name || c.customer_no }}</td>
                        <td style="text-align:right;">{{ formatMoney(c.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Transactions -->
              <div class="panel">
                <div class="warn-title">Transactions ({{ drillData.transactions?.length || 0 }})</div>

                <div class="table-scroll">
                  <table class="mini-table">
                    <thead>
                      <tr>
                        <th style="width:110px;">Date</th>
                        <th>Description</th>
                        <th style="width:120px;">Vendor</th>
                        <th style="width:120px;">Customer</th>
                        <th style="width:120px;">Invoice</th>
                        <th style="width:140px; text-align:right;">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="t in drillData.transactions" :key="t.id || (t.docnumber + '_' + t.datum)">
                        <td>{{ fmtDate(t.datum) }}</td>
                        <td>{{ t.oms25 || "" }}</td>
                        <td>{{ t.vendor_name || t.vendor_no || "" }}</td>
                        <td>{{ t.customer_name || t.customer_no || "" }}</td>
                        <td>{{ t.faktuurnr || t.docnumber || "" }}</td>
                        <td style="text-align:right;">{{ formatMoney(t.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            </div>

          </div>
        </div>



  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PnlCfoReportPage",

  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");

    // default = current month
    const first = `${yyyy}-${mm}-01`;
    const last = this.lastDayOfMonth(yyyy, Number(mm));

    return {
        startDate: first,
        endDate: last,
        cu: "ALL",

        showPercent: true,
        showDetails: false,

        loading: false,
        error: null,
        mtdReport: null,
        ytdReport: null,
        lytdReport: null,

        drillOpen: false,
        drillLoading: false,
        drillError: null,
        drillData: null,
        drillRow: null,
        drillPeriod: "MTD"


    };
  },

  computed: {
    canRun() {
      return !!this.startDate && !!this.endDate && String(this.cu || "").length > 0;
    },

    reportLoaded() {
      return !!this.mtdReport && !!this.ytdReport && !!this.lytdReport;
    },

    monthObj() {
      const m = this.mtdReport?.months?.[0] || null;
      return this.collapseLinesForMonth(m);
    },

    displayMonthLabel() {
      const m = this.monthObj?.month_start;
      return m ? this.formatMonthLabelShort(m) : "";
    },

    ytdTotalsObj() {
      const summed = this.sumMonthsToPeriod(this.ytdReport?.months || []);
      return this.collapseLinesForMonth(summed);
    },


    lytdTotalsObj() {
      const summed = this.sumMonthsToPeriod(this.lytdReport?.months || []);
      return this.collapseLinesForMonth(summed);
    },


    collapsedGroups() {
      return [
        {
          key: "SALARIES_OFFICE",
          label: "SALARIES - OFFICE",
          targetAcct: "54900",
          accounts: ["54900", "55800", "55902", "55903", "56000"]
        }
      ];
    },

    drillRowLabel() {
      if (!this.drillRow) return "";
      // row.label includes "(acct)" already for details
      return this.drillRow.label || "";
    },

    drillParams() {
      const p = this.drillPeriod || "MTD";
      const y = String(this.startDate).slice(0, 4);

      if (p === "YTD") {
        return { startDate: `${y}-01-01`, endDate: this.endDate };
      }
      if (p === "LYTD") {
        const ly = String(Number(y) - 1);
        return { startDate: `${ly}-01-01`, endDate: `${ly}${String(this.endDate).slice(4)}` };
      }
      return { startDate: this.startDate, endDate: this.endDate }; // MTD
    },

  

    unmappedAccounts() {
      const s = new Set();
      (this.mtdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      (this.ytdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      (this.lytdReport?.unmapped_accounts || []).forEach(a => s.add(a));
      return Array.from(s).sort();
    },

    periodsForDetails() {
      const out = [];
      if (this.monthObj) out.push(this.monthObj);
      if (this.ytdTotalsObj) out.push(this.ytdTotalsObj);
      if (this.lytdTotalsObj) out.push(this.lytdTotalsObj);
      return out;
    },


    months() {
      return this.mtdReport?.months || [];
    },


    // Fixed “Excel skeleton” rows (always)
    baseRows() {
      return [
        { key: "gross_sales", label: "Gross Sales", type: "month", bold: true, percentBase: "GROSS_SALES" },
        { key: "contra_sales", label: "Contra Sales", type: "month", percentBase: "GROSS_SALES" },
        { key: "net_sales", label: "Net Sales", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },

        { key: "cogs_hdr", label: "Cost of Goods Sold", type: "header" },
        { key: "base_cost_of_sales", label: "  Base Cost of Sales", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "variances_to_standard", label: "  Variances to Standard", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "direct_labor_variance", label: "  Direct Labor Variance", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "overhead_variance", label: "  Overhead Variance", type: "cogs", indent: true, percentBase: "GROSS_SALES" },
        { key: "total_cogs", label: "Total COGS", type: "month", bold: true, percentBase: "GROSS_SALES" },

        { key: "gross_profit", label: "Gross Profit", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },
        { key: "overhead_263a", label: "263A Overhead", type: "month", percentBase: "GROSS_SALES" },
        { key: "gross_profit_after_263a", label: "Gross Profit After 263A", type: "month", bold: true, doubleTop: true, percentBase: "GROSS_SALES" },

        { key: "operating_expenses", label: "Operating Expenses", type: "month", percentBase: "NET_SALES" },
        { key: "overhead_and_other_credits", label: "Overhead & Other Credits", type: "month", percentBase: "NET_SALES" },
        { key: "net_operating_expenses", label: "Net Operating Expenses", type: "month", bold: true, percentBase: "NET_SALES" },

        { key: "depr_addback", label: "Depreciation (Add-back)", type: "calc", indent: true, percentBase: "NET_SALES" },
        { key: "operating_profit_before_depr", label: "Operating Profit/Loss (Before Depreciation)", type: "calc", bold: true, doubleTop: true, percentBase: "NET_SALES" },

        { key: "other_income_hdr", label: "Other Income", type: "header" },
        { key: "other_income", label: "Total Other Income", type: "month", bold: true, percentBase: "NET_SALES" },

        { key: "other_expense_hdr", label: "Other Expense", type: "header" },
        { key: "other_expense", label: "Total Other Expense", type: "month", bold: true, percentBase: "NET_SALES" },

        { key: "depreciation", label: "Depreciation", type: "month", percentBase: "NET_SALES" },

        { key: "net_income_before_taxes", label: "Net Income (Loss) Before Taxes", type: "month", bold: true, doubleTop: true, percentBase: "NET_SALES" },

        { key: "taxes_hdr", label: "Taxes", type: "header" },
        { key: "taxes", label: "Total Taxes", type: "month", bold: true, percentBase: "NET_SALES" },

        { key: "net_income", label: "NET INCOME", type: "month", bold: true, doubleTop: true, percentBase: "NET_SALES" }
      ];
    },

    // Build a stable set of detail rows across all months by account
    detailRows() {
      if (!this.showDetails) return [];

      const wantSections = new Set(["SALES", "COGS", "OPEX", "OTHER_INCOME", "OTHER_EXPENSE", "TAX"]);
      const bySection = new Map();

      for (const m of this.periodsForDetails) {
        for (const ln of (m.lines || [])) {
          if (!wantSections.has(ln.section)) continue;
          const sec = ln.section;

          if (!bySection.has(sec)) bySection.set(sec, new Map());

          const secMap = bySection.get(sec);
          const acct = (ln?.acct_no == null) ? "" : `${ln.acct_no}`.trim();
          if (!acct) continue;

          // ✅ skip only the non-primary members (keep primary acct so SALARIES - OFFICE still shows)
          if (this.isCollapsedMemberAcct(acct) && !this.isCollapsedPrimaryAcct(acct)) continue;



          if (!secMap.has(acct)) {
            secMap.set(acct, {
              key: `DETAIL:${sec}:${acct}`,
              label: `${ln.line_name || ln.acct_desc || acct} (${acct})`,
              type: "detail",
              section: sec,
              acct_no: acct,
              sort_order: Number(ln.sort_order ?? 999999),
              indent: true
            });
          }

        }
      }

      // Convert to sorted arrays, keep section order consistent
      const sectionOrder = ["SALES", "COGS", "OPEX", "OTHER_INCOME", "OTHER_EXPENSE", "TAX"];
      const out = [];

      for (const sec of sectionOrder) {
        const secMap = bySection.get(sec);
        if (!secMap) continue;

        const rows = Array.from(secMap.values()).sort((a, b) => {
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
          return a.acct_no.localeCompare(b.acct_no);
        });

        out.push({ key: `HDR:${sec}`, label: this.sectionLabel(sec), type: "subheader" });
        out.push(...rows);
      }

      return out;
    },

    // Inject details under the matching skeleton areas
    renderedRows() {
      const base = [...this.baseRows];

      if (!this.showDetails) {
        return base;
      }


      // Where to inject each section’s detail block
      const injectAfterKey = {
        gross_sales: "SALES",
        cogs_hdr: "COGS",
        operating_expenses: "OPEX",
        other_income_hdr: "OTHER_INCOME",
        other_expense_hdr: "OTHER_EXPENSE",
        taxes_hdr: "TAX"
      };

      // Build lookup for detail blocks by section
      const blocks = new Map();
      for (const r of this.detailRows) {
        if (r.type === "subheader") {
          const sec = r.key.replace("HDR:", "");
          blocks.set(sec, []);
        } else if (r.type === "detail") {
          if (!blocks.has(r.section)) blocks.set(r.section, []);
          blocks.get(r.section).push(r);
        }
      }

      const out = [];
      for (const row of base) {
        out.push(row);

        const sec = injectAfterKey[row.key];
        if (sec && blocks.has(sec)) {
          // Add subheader then details (subheader only if there are rows)
          const details = blocks.get(sec) || [];
          if (details.length > 0) {
            out.push({ key: `SUBHDR:${sec}:${row.key}`, label: `${this.sectionLabel(sec)} Details`, type: "subheader", indent: true });
            out.push(...details);
          }
        }
      }

      // ✅ STOP after Operating Profit/Loss
      return out;

    }
  },

  methods: {
  lastDayOfMonth(y, m) {
    const d = new Date(y, m, 0);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  },


    sectionLabel(sec) {
      if (sec === "SALES") return "Sales";
      if (sec === "COGS") return "COGS";
      if (sec === "OPEX") return "Operating Expenses";
      if (sec === "OTHER_INCOME") return "Other Income";
      if (sec === "OTHER_EXPENSE") return "Other Expense";
      if (sec === "TAX") return "Taxes";
      return sec;
    },

    async runReport() {
      if (this.loading) return;
      this.error = null;
      this.mtdReport = null;
      this.ytdReport = null;
      this.lytdReport = null;

      if (!this.canRun) return;

      this.loading = true;

      try {
        // MTD
        const mtdParams = { startDate: this.startDate, endDate: this.endDate, cu: this.cu };

        // YTD (Jan 1 → endDate)
        const y = String(this.startDate).slice(0, 4);
        const ytdParams = { startDate: `${y}-01-01`, endDate: this.endDate, cu: this.cu };

        // LYTD (prior year Jan 1 → prior year same end date)
        const ly = String(Number(y) - 1);
        const lytdParams = {
          startDate: `${ly}-01-01`,
          endDate: `${ly}${String(this.endDate).slice(4)}`, // keep -MM-DD
          cu: this.cu
        };

        // instead of Promise.all([...])
        const mtdRes = await axios.get("/pnlCfoReport", { params: mtdParams });
        this.mtdReport = mtdRes.data;

        const ytdRes = await axios.get("/pnlCfoReport", { params: ytdParams });
        this.ytdReport = ytdRes.data;

        const lytdRes = await axios.get("/pnlCfoReport", { params: lytdParams });
        this.lytdReport = lytdRes.data;

      } catch (err) {
        console.error("runReport error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load P&L report.";
      } finally {
        this.loading = false;
      }
    },

    formatMonthLabel(iso) {
      // iso like "2025-09-01"
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      const mo = d.toLocaleString("en-US", { month: "short" });
      const yr = d.getFullYear();
      return `${mo} ${yr}`;
    },

    formatMonthLabelShort(iso) {
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      const mo = d.toLocaleString("en-US", { month: "short" });
      const yy = String(d.getFullYear()).slice(-2);
      return `${mo}-${yy}`;
    },


    formatMoney(v) {
      const n = Number(v);
      if (!isFinite(n)) return "";
      const abs = Math.abs(n);
      const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return n < 0 ? `(${formatted})` : formatted;
    },

    formatPercent(v) {
      const n = Number(v);
      if (!isFinite(n)) return "";
      return `${n.toFixed(2)}%`;
    },

    // Return amount for this row+month (or null for header rows)
    cellAmount(row, month) {
      if (!row || !month) return null;

      if (row.type === "header" || row.type === "subheader") return null;

      if (row.type === "month") {
        const val = month[row.key];
        return typeof val === "number" ? val : (val == null ? 0 : Number(val));
      }

      if (row.type === "cogs") {
        const val = month?.cogs_breakdown?.[row.key];
        return typeof val === "number" ? val : (val == null ? 0 : Number(val));
      }

      if (row.type === "detail") {
        const acct = String(row.acct_no || "").trim();
        const ln = (month.lines || []).find(x => String(x.acct_no || "").trim() === acct);
        if (!ln) return 0;
        return typeof ln.amount === "number" ? ln.amount : Number(ln.amount || 0);
      }

      if (row.type === "calc") {
        // Depreciation account (from detail lines)
        const depLine = (month.lines || []).find(x => String(x.acct_no || "").trim() === "51400");
        const depAmt = Number(depLine?.amount || 0); // this will be NEGATIVE as an expense

        if (row.key === "depr_addback") {
          // show positive add-back
          return -depAmt;
        }

        if (row.key === "operating_profit_before_depr") {
          const gp = Number(month?.gross_profit_after_263a || 0);
          const noe = Number(month?.net_operating_expenses || 0);

          // add depreciation back (i.e., remove it from NOE)
          return (gp + noe) - depAmt; // depAmt is negative, so subtracting it increases profit
        }

        return 0;
      }




      return null;
    },

    // Percent for this row+month (null if not applicable)
    cellPercent(row, period) {
      if (!this.showPercent) return null;
      if (!row || !period) return null;

      if (row.type === "header" || row.type === "subheader") return null;

      // detail rows: compute ourselves to support net sales base
      let amt = Number(this.cellAmount(row, period) || 0);

      // choose denominator
      const gross = Number(period.gross_sales || 0);
      const net = Number(period.net_sales || 0);

      const isSalesRow =
        row.key === "gross_sales" ||
        row.key === "contra_sales" ||
        row.key === "net_sales" ||
        (row.type === "detail" && row.section === "SALES");

      const denom = isSalesRow ? gross : net;

      if (!isFinite(denom) || Math.abs(denom) < 0.0001) return null;
      return (amt / denom) * 100;
    },

    rowClass(row) {
      if (!row) return "";
      return {
        "row-header": row.type === "header",
        "row-subheader": row.type === "subheader",
        "row-bold": !!row.bold,
        "row-double-top": !!row.doubleTop
      };
    },

    isCollapsedPrimaryAcct(acctNoLike) {
      let acct = acctNoLike;
      if (acct && typeof acct === "object") {
        acct = acct.acct_no ?? acct.acctNo ?? acct.account ?? "";
      }
      const a = (acct == null) ? "" : `${acct}`.trim();
      if (!a) return false;

      // primary = first acct in the group accounts array
      return this.collapsedGroups.some(g => String(g.accounts?.[0] || "").trim() === a);
    },


    isCollapsedMemberAcct(acctNoLike) {
      let acct = acctNoLike;

      // if they accidentally pass the whole line object
      if (acct && typeof acct === "object") {
        acct = acct.acct_no ?? acct.acctNo ?? acct.account ?? "";
      }

      const a = (acct == null) ? "" : `${acct}`.trim();
      if (!a) return false;

      return this.collapsedGroups.some(g => g.accounts.includes(a));
    },

    collapseLinesForMonth(month) {
      if (!month || !Array.isArray(month.lines)) return month;

      const out = { ...month, lines: [...month.lines] };

      for (const grp of this.collapsedGroups) {
        const set = new Set(grp.accounts);

        const members = out.lines.filter(l => set.has(String(l.acct_no || "").trim()));
        if (!members.length) continue;

        // remove members
        out.lines = out.lines.filter(l => !set.has(String(l.acct_no || "").trim()));

        // pick a “primary” acct to represent the group in display (use first in grp.accounts)
        const primaryAcct = grp.accounts[0];
        const primary = members.find(x => String(x.acct_no || "").trim() === primaryAcct) || members[0];

        const sumAmt = members.reduce((s, x) => s + Number(x.amount || 0), 0);
        const sumRaw = members.reduce((s, x) => s + Number(x.raw_amount || 0), 0);

        const collapsed = {
          ...primary,
          // keep acct_no real-ish (so anything downstream that keys off acct_no still works)
          acct_no: primaryAcct,
          acct_desc: grp.label,
          line_name: grp.label,

          // stable identity + drilldown
          _groupKey: grp.key,
          _isCollapsedGroup: true,
          _components: members,

          amount: sumAmt,
          raw_amount: sumRaw,

          // let frontend recompute percent using your rules
          percent_of_gross_sales: null,

          // keep sort stable (use smallest sort among members or a hardcoded number)
          sort_order: Math.min(...members.map(x => Number(x.sort_order ?? 999999)))
        };

        out.lines.push(collapsed);
      }

      out.lines.sort((a, b) => {
        const sa = String(a.section || "").localeCompare(String(b.section || ""));
        if (sa !== 0) return sa;
        const so = Number(a.sort_order ?? 999999) - Number(b.sort_order ?? 999999);
        if (so !== 0) return so;
        return String(a.acct_no || "").localeCompare(String(b.acct_no || ""));
      });

      return out;
    },

    sumMonthsToPeriod(months) {
      if (!months || !months.length) return null;

      const sum = (arr, fn) => arr.reduce((s, x) => s + (Number(fn(x)) || 0), 0);

      const out = {
        // month_start is just a label placeholder for the UI
        month_start: months[months.length - 1].month_start,

        gross_sales: sum(months, m => m.gross_sales),
        contra_sales: sum(months, m => m.contra_sales),
        net_sales: sum(months, m => m.net_sales),

        total_cogs: sum(months, m => m.total_cogs),
        gross_profit: sum(months, m => m.gross_profit),
        overhead_263a: sum(months, m => m.overhead_263a),
        gross_profit_after_263a: sum(months, m => m.gross_profit_after_263a),

        operating_expenses: sum(months, m => m.operating_expenses),
        overhead_and_other_credits: sum(months, m => m.overhead_and_other_credits),
        net_operating_expenses: sum(months, m => m.net_operating_expenses),

        other_income: sum(months, m => m.other_income),
        other_expense: sum(months, m => m.other_expense),
        depreciation: sum(months, m => m.depreciation),
        net_income_before_taxes: sum(months, m => m.net_income_before_taxes),
        taxes: sum(months, m => m.taxes),
        net_income: sum(months, m => m.net_income),

        cogs_breakdown: {
          base_cost_of_sales: sum(months, m => m.cogs_breakdown?.base_cost_of_sales),
          variances_to_standard: sum(months, m => m.cogs_breakdown?.variances_to_standard),
          total_actual_labor: sum(months, m => m.cogs_breakdown?.total_actual_labor),
          total_standard_labor_absorbed: sum(months, m => m.cogs_breakdown?.total_standard_labor_absorbed),
          direct_labor_variance: sum(months, m => m.cogs_breakdown?.direct_labor_variance),
          overhead: sum(months, m => m.cogs_breakdown?.overhead),
          total_overhead_absorbed: sum(months, m => m.cogs_breakdown?.total_overhead_absorbed),
          overhead_variance: sum(months, m => m.cogs_breakdown?.overhead_variance),
          total_cogs: sum(months, m => m.cogs_breakdown?.total_cogs)
        },

        // detail rows (account lines): aggregate by acct_no
        lines: []
      };

      // Merge detail lines by acct_no
      const lineMap = new Map();
      for (const m of months) {
        for (const ln of (m.lines || [])) {
          const acct = String(ln.acct_no || "").trim();
          if (!acct) continue;

          if (!lineMap.has(acct)) {
            lineMap.set(acct, { ...ln, amount: 0, raw_amount: 0 });
          }
          const cur = lineMap.get(acct);
          cur.amount += Number(ln.amount) || 0;
          cur.raw_amount += Number(ln.raw_amount) || 0;
        }
      }

      out.lines = Array.from(lineMap.values());

      return out;
    },

    isClickable(row) {
      // only detail rows (real GL accounts) for now
      return row && row.type === "detail" && String(row.acct_no || "").trim().length > 0;
    },

    async openDrilldown(row, period) {
      if (!this.isClickable(row)) return;

      this.drillOpen = true;
      this.drillLoading = true;
      this.drillError = null;
      this.drillData = null;
      this.drillRow = row;
      this.drillPeriod = period || "MTD";

      try {
        const acct = String(row.acct_no || "").trim();
        const { startDate, endDate } = this.drillParams;

        const { data } = await axios.get("/pnlDrilldown", {
          params: { acct, startDate, endDate, cu: this.cu, limit: 200 }
        });

        this.drillData = data;
      } catch (err) {
        console.error("openDrilldown error:", err);
        this.drillError = err?.response?.data?.error || err?.response?.data?.details || "Failed to load drilldown.";
      } finally {
        this.drillLoading = false;
      }
    },

    closeDrilldown() {
      this.drillOpen = false;
      this.drillLoading = false;
      this.drillError = null;
      this.drillData = null;
      this.drillRow = null;
      this.drillPeriod = "MTD";
    },

    fmtDate(v) {
      if (!v) return "";
      // works if backend returns Date or string
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },

    printPNL() {
      if (!this.reportLoaded) {
        this.error = "Run the report before printing.";
        return;
      }

      const payload = {
        meta: {
          generatedAt: new Date().toISOString(),
          cu: this.cu,
          startDate: this.startDate,
          endDate: this.endDate,
          showPercent: this.showPercent,
          showDetails: this.showDetails,
          displayMonthLabel: this.displayMonthLabel
        },

        ranges: {
          mtd: {
            startDate: this.mtdReport?.startDate || this.startDate,
            endDate: this.mtdReport?.endDate || this.endDate
          },
          ytd: {
            startDate: this.ytdReport?.startDate || `${String(this.startDate).slice(0, 4)}-01-01`,
            endDate: this.ytdReport?.endDate || this.endDate
          },
          lytd: {
            startDate: this.lytdReport?.startDate || "",
            endDate: this.lytdReport?.endDate || ""
          }
        },

        rows: this.renderedRows.map(row => ({
          key: row.key,
          label: row.label,
          type: row.type,
          indent: !!row.indent,
          bold: !!row.bold,
          doubleTop: !!row.doubleTop,

          mtdAmount: this.cellAmount(row, this.monthObj),
          mtdPercent: this.showPercent ? this.cellPercent(row, this.monthObj) : null,

          ytdAmount: this.cellAmount(row, this.ytdTotalsObj),
          ytdPercent: this.showPercent ? this.cellPercent(row, this.ytdTotalsObj) : null,

          lytdAmount: this.cellAmount(row, this.lytdTotalsObj),
          lytdPercent: this.showPercent ? this.cellPercent(row, this.lytdTotalsObj) : null
        })),

        unmappedAccounts: this.unmappedAccounts || []
      };

      sessionStorage.setItem("pablo_cfo_pnl_print_payload", JSON.stringify(payload));

      const routeData = this.$router.resolve({
        path: "/print-cfo-pnl-report"
      });

      window.open(routeData.href, "_blank");
    },



    exportCSV() {
      if (!this.reportLoaded) return;

      const months = this.months || [];
      const rows = [];

      // Header row
      const header = ["Line"];
      for (const m of months) {
        header.push(this.formatMonthLabel(m.month_start));
        if (this.showPercent) header.push("%");
      }
      rows.push(header);

      // Data rows
      for (const r of this.renderedRows) {
        const line = [r.label];

        for (const m of months) {
          const amt = this.cellAmount(r, m);
          const pct = this.showPercent ? this.cellPercent(r, m) : null;

          line.push(amt == null ? "" : Number(amt).toFixed(2));
          if (this.showPercent) line.push(pct == null ? "" : Number(pct).toFixed(2));
        }

        rows.push(line);
      }

      const csv = rows
        .map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `pnl_${this.cu}_${this.startDate}_${this.endDate}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
/* ✅ PAGE BASE — Pablo black */
.pnl-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* ✅ BASE CONTAINER — matches JIMScheduling */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* ✅ Header row */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* ✅ MAIN LAYOUT */
.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* ✅ Left-side controls */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 120px;
}

/* ✅ Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* ✅ Scrollable sheet area */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}
.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}
.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* ✅ Panel base */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.select-input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* ✅ Toggles */
.toggle-row {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.toggle {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 13px;
  color: #cbbfbf;
}

.toggle input {
  transform: translateY(1px);
}

/* ✅ Buttons */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* ✅ Error / warn boxes */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.warn-box {
  border-color: #888844;
  background: #1f1a0b;
  color: #fef3c7;
  width: 100%;
}

.warn-title {
  font-weight: bold;
  margin-bottom: 6px;
}

.warn-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.warn-chip {
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #665;
  background: #120f08;
  font-size: 12px;
}

/* ✅ Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
  word-break: break-word;
}

/* table container */
.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

.hint-panel {
  margin-top: 20px;
  background-color: #0b0101;
}

/* ✅ Table scroll horizontal */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

/* ✅ P&L table */
.pnl-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

/* base cells */
.pnl-table th,
.pnl-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
}

/* line col left */
.line-col {
  text-align: left !important;
  min-width: 260px;
}

/* header row */
.pnl-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

/* sticky left column */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  color: #cbbfbf;
}

/* row hover */
.pnl-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/* columns */
.month-col { min-width: 120px; }
.pct-col { min-width: 60px; }

/* indents */
.indent {
  padding-left: 18px;
  display: inline-block;
}

/* formatting rows */
.row-header td {
  background: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  text-align: left !important;
}

.row-subheader td {
  background: rgba(0,0,0,0.35);
  color: #fff;
  font-weight: bold;
  text-align: left !important;
}

.row-bold td {
  font-weight: bold;
}

.row-double-top td {
  border-top: 3px double #cbbfbf !important;
}

.amt-cell { text-align: right; }
.pct-cell { text-align: right; }

.dash {
  opacity: 0.6;
}

.small-muted {
  font-size: 12px;
  opacity: 0.85;
  color: #fff;
}

/* empty row */
.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.8;
  text-align: center !important;
}

.clickable-cell {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.clickable-cell:hover {
  background-color: #5595b7c2;
  color: #fff;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 14px;
}

.modal {
  width: min(1200px, 96vw);
  max-height: 92vh;
  overflow: auto;
  border-radius: 12px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-title {
  font-size: 18px;
  font-weight: bold;
}

.modal-close {
  border: 1px solid #888;
  background: #111;
  color: #cbbfbf;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.mini-table th,
.mini-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
}

.mini-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  position: sticky;
  top: 0;
}


/* ✅ PRINT */
@media print {
  .left-side-section,
  .error-box,
  .warn-box {
    display: none !important;
  }

  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  .sheet-scroll-container {
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }

  .jim-scheduling-container,
  .panel,
  .summary-bar {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  .pnl-table th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  .pnl-table td, .pnl-table th {
    border: 1px solid #999 !important;
  }
}
</style>
